import React, { useState } from "react";
import { Button, ButtonGroup, Badge, Form, Modal } from "react-bootstrap";
import { TaskModal } from "../../components/work/TaskModal";
import { ProjectModal } from "../../components/work/ProjectModal";
import { TaskDetailsModal } from "../../components/work/TaskDetailsModal";
import { Task, Project } from "../../types/Work";

export default function Components() {
  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);

  // Sample data for modals
  const sampleProjects: Project[] = [
    {
      id: "proj-1",
      name: "Mobile App",
      description: "iOS & Android development",
      color: "#007aff",
      createdAt: new Date(),
      updatedAt: new Date(),
      ownerId: "user-1",
    },
    {
      id: "proj-2",
      name: "Website Redesign",
      description: "Complete UI/UX overhaul",
      color: "#34c759",
      createdAt: new Date(),
      updatedAt: new Date(),
      ownerId: "user-1",
    },
  ];

  const sampleTask: Task = {
    id: "task-1",
    projectId: "proj-1",
    name: "Design new onboarding flow",
    descriptionHtml:
      "<p>Create wireframes and mockups for the new user onboarding experience.</p><ul><li>User research</li><li>Wireframes</li><li>High-fidelity mockups</li></ul>",
    status: "in_progress",
    priority: "high",
    subtasks: [
      { id: "sub-1", title: "User research", done: true },
      { id: "sub-2", title: "Create wireframes", done: false },
      { id: "sub-3", title: "Design mockups", done: false },
    ],
    timeEntries: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  return (
    <div className="work-page">
      {/* Header */}
      <div className="work-header">
        <h1>Components Showcase</h1>
        <p>iOS-inspired UI component library</p>
      </div>

      {/* Buttons Section */}
      <section className="component-section">
        <h2 className="section-title">Buttons</h2>

        <div className="component-group">
          <h3 className="group-title">Primary Buttons</h3>
          <div className="component-demo">
            <Button variant="primary" size="lg">
              <i className="fas fa-plus"></i> Large Primary
            </Button>
            <Button variant="primary">
              <i className="fas fa-check"></i> Default Primary
            </Button>
            <Button variant="primary" size="sm">
              <i className="fas fa-star"></i> Small Primary
            </Button>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Secondary Buttons</h3>
          <div className="component-demo">
            <Button variant="outline-primary" size="lg">
              <i className="fas fa-folder-plus"></i> Large Secondary
            </Button>
            <Button variant="outline-primary">
              <i className="fas fa-pencil"></i> Default Secondary
            </Button>
            <Button variant="outline-primary" size="sm">
              <i className="fas fa-trash"></i> Small Secondary
            </Button>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Destructive Buttons</h3>
          <div className="component-demo">
            <Button variant="danger">
              <i className="fas fa-trash"></i> Delete
            </Button>
            <Button variant="outline-danger">
              <i className="fas fa-xmark"></i> Cancel
            </Button>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Button Groups</h3>
          <div className="component-demo">
            <ButtonGroup>
              <Button variant="primary">All</Button>
              <Button variant="outline-primary">Active</Button>
              <Button variant="outline-primary">Completed</Button>
              <Button variant="outline-primary">Archived</Button>
            </ButtonGroup>
          </div>
        </div>
      </section>

      {/* Badges Section */}
      <section className="component-section">
        <h2 className="section-title">Badges</h2>

        <div className="component-group">
          <h3 className="group-title">Priority Badges</h3>
          <div className="component-demo">
            <Badge bg="danger" className="priority-badge">
              High Priority
            </Badge>
            <Badge bg="warning" className="priority-badge">
              Medium Priority
            </Badge>
            <Badge bg="secondary" className="priority-badge">
              Low Priority
            </Badge>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Status Badges</h3>
          <div className="component-demo">
            <Badge bg="primary">Todo</Badge>
            <Badge bg="info">In Progress</Badge>
            <Badge bg="warning">Review</Badge>
            <Badge bg="success">Done</Badge>
            <Badge bg="secondary">Archived</Badge>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Pill Badges</h3>
          <div className="component-demo">
            <Badge pill bg="primary">
              New
            </Badge>
            <Badge pill bg="success">
              Active
            </Badge>
            <Badge pill bg="danger">
              Urgent
            </Badge>
            <Badge pill bg="warning">
              Pending
            </Badge>
          </div>
        </div>
      </section>

      {/* Cards Section */}
      <section className="component-section">
        <h2 className="section-title">Cards</h2>

        <div className="component-group">
          <h3 className="group-title">Task Cards</h3>
          <div className="component-demo cards-grid">
            {/* High Priority Task */}
            <div className="task-card">
              <div className="task-card-content">
                <h4 className="task-title">Design new dashboard</h4>
                <p className="task-description-excerpt">
                  Create wireframes and mockups for the analytics dashboard with
                  user research and interactive prototypes
                </p>

                <div className="task-project-name">
                  <i className="fas fa-folder"></i>
                  <span>Mobile App</span>
                </div>

                {/* Priority and Progress Row */}
                <div className="task-priority-progress-row">
                  {/* Priority Dots */}
                  <div className="task-priority-dots">
                    <span className="priority-label">Priority:</span>
                    <div className="dots-container">
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ff9500", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ff9500", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ff9500", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ff9500", opacity: 0.3 }}
                      ></span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="task-progress-section">
                    <div className="progress-header">
                      <span className="progress-label">Progress</span>
                      <span className="progress-text">2/5 tasks</span>
                    </div>
                    <div className="progress" style={{ height: "6px" }}>
                      <div
                        className="progress-bar"
                        style={{
                          width: "40%",
                          backgroundColor: "#007aff",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="task-footer">
                  <div className="task-meta-item">
                    <i className="fas fa-clock"></i>
                    <span>Created 2 days ago</span>
                  </div>
                  <div className="task-meta-item deadline">
                    <i className="fas fa-flag"></i>
                    <span>Due in 3 days</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Medium Priority Task */}
            <div className="task-card">
              <div className="task-card-content">
                <h4 className="task-title">API Integration</h4>
                <p className="task-description-excerpt">
                  Connect frontend with backend services and implement
                  authentication flow
                </p>

                <div className="task-project-name">
                  <i className="fas fa-folder"></i>
                  <span>Website Redesign</span>
                </div>

                {/* Priority and Progress Row */}
                <div className="task-priority-progress-row">
                  {/* Priority Dots - Medium */}
                  <div className="task-priority-dots">
                    <span className="priority-label">Priority:</span>
                    <div className="dots-container">
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ffcc00", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ffcc00", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ffcc00", opacity: 0.3 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ffcc00", opacity: 0.3 }}
                      ></span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="task-progress-section">
                    <div className="progress-header">
                      <span className="progress-label">Progress</span>
                      <span className="progress-text">1/3 tasks</span>
                    </div>
                    <div className="progress" style={{ height: "6px" }}>
                      <div
                        className="progress-bar"
                        style={{
                          width: "33%",
                          backgroundColor: "#007aff",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="task-footer">
                  <div className="task-meta-item">
                    <i className="fas fa-clock"></i>
                    <span>Created 5 hours ago</span>
                  </div>
                  <div className="task-meta-item deadline">
                    <i className="fas fa-flag"></i>
                    <span>Due in 8 days</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Low Priority Task */}
            <div className="task-card">
              <div className="task-card-content">
                <h4 className="task-title">Documentation Update</h4>
                <p className="task-description-excerpt">
                  Update project documentation and API specifications for the
                  new features
                </p>

                <div className="task-project-name">
                  <i className="fas fa-folder"></i>
                  <span>Mobile App</span>
                </div>

                {/* Priority and Progress Row */}
                <div className="task-priority-progress-row">
                  {/* Priority Dots - Low */}
                  <div className="task-priority-dots">
                    <span className="priority-label">Priority:</span>
                    <div className="dots-container">
                      <span
                        className="dot"
                        style={{ backgroundColor: "#8e8e93", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#8e8e93", opacity: 0.3 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#8e8e93", opacity: 0.3 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#8e8e93", opacity: 0.3 }}
                      ></span>
                    </div>
                  </div>

                  {/* No Progress Bar - No Subtasks */}
                  <div className="task-progress-section">
                    <div className="progress-header">
                      <span className="progress-label">No subtasks</span>
                    </div>
                  </div>
                </div>

                <div className="task-footer">
                  <div className="task-meta-item">
                    <i className="fas fa-clock"></i>
                    <span>Created 1 week ago</span>
                  </div>
                  <div className="task-meta-item deadline">
                    <i className="fas fa-flag"></i>
                    <span>Due in 15 days</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Urgent Priority Task */}
            <div className="task-card">
              <div className="task-card-content">
                <h4 className="task-title">Critical Bug Fix</h4>
                <p className="task-description-excerpt">
                  Fix authentication issue causing users to be logged out
                  unexpectedly during checkout
                </p>

                <div className="task-project-name">
                  <i className="fas fa-folder"></i>
                  <span>E-commerce Platform</span>
                </div>

                {/* Priority and Progress Row */}
                <div className="task-priority-progress-row">
                  {/* Priority Dots - Urgent */}
                  <div className="task-priority-dots">
                    <span className="priority-label">Priority:</span>
                    <div className="dots-container">
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ff3b30", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ff3b30", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ff3b30", opacity: 1 }}
                      ></span>
                      <span
                        className="dot"
                        style={{ backgroundColor: "#ff3b30", opacity: 1 }}
                      ></span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="task-progress-section">
                    <div className="progress-header">
                      <span className="progress-label">Progress</span>
                      <span className="progress-text">0/2 tasks</span>
                    </div>
                    <div className="progress" style={{ height: "6px" }}>
                      <div
                        className="progress-bar"
                        style={{
                          width: "0%",
                          backgroundColor: "#007aff",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="task-footer">
                  <div className="task-meta-item">
                    <i className="fas fa-clock"></i>
                    <span>Created 3 hours ago</span>
                  </div>
                  <div className="task-meta-item deadline urgent">
                    <i className="fas fa-flag"></i>
                    <span>Due in 6 hours</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Project Cards</h3>
          <div className="component-demo cards-grid">
            {/* Mobile App Project */}
            <div className="project-card">
              <div
                className="project-color-bar"
                style={{ background: "#007aff" }}
              ></div>
              <div className="project-card-content">
                <div className="project-header">
                  <h4 className="project-title">Mobile App</h4>
                </div>
                <p className="project-description">
                  iOS & Android development with React Native and native modules
                </p>

                <div className="project-stats-row">
                  <div className="stat-card">
                    <div
                      className="stat-circle"
                      style={{
                        background:
                          "conic-gradient(#007aff 0deg 241deg, rgba(255, 255, 255, 0.1) 241deg)",
                      }}
                    >
                      <div className="stat-circle-inner">
                        <span className="stat-number">8</span>
                        <span className="stat-total">/12</span>
                      </div>
                    </div>
                    <span className="stat-label">Tasks Done</span>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-card">
                    <div className="stat-icon-wrapper">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="stat-text">
                      <span className="stat-number">42h</span>
                      <span className="stat-label">Time Logged</span>
                    </div>
                  </div>
                </div>

                <div className="project-progress">
                  <div className="progress-info">
                    <span className="progress-label">Overall Progress</span>
                    <span className="progress-percentage">67%</span>
                  </div>
                  <div className="progress" style={{ height: "8px" }}>
                    <div
                      className="progress-bar"
                      style={{
                        width: "67%",
                        background: "linear-gradient(90deg, #007aff, #5856d6)",
                      }}
                    ></div>
                  </div>
                </div>

                <div className="project-footer">
                  <div className="project-meta-item">
                    <i className="fas fa-calendar"></i>
                    <span>Updated 2 hours ago</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Website Redesign Project */}
            <div className="project-card">
              <div
                className="project-color-bar"
                style={{ background: "#34c759" }}
              ></div>
              <div className="project-card-content">
                <div className="project-header">
                  <h4 className="project-title">Website Redesign</h4>
                </div>
                <p className="project-description">
                  Complete UI/UX overhaul with modern design system and
                  accessibility improvements
                </p>

                <div className="project-stats-row">
                  <div className="stat-card">
                    <div
                      className="stat-circle"
                      style={{
                        background:
                          "conic-gradient(#34c759 0deg 137deg, rgba(255, 255, 255, 0.1) 137deg)",
                      }}
                    >
                      <div className="stat-circle-inner">
                        <span className="stat-number">3</span>
                        <span className="stat-total">/8</span>
                      </div>
                    </div>
                    <span className="stat-label">Tasks Done</span>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-card">
                    <div className="stat-icon-wrapper">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="stat-text">
                      <span className="stat-number">18h</span>
                      <span className="stat-label">Time Logged</span>
                    </div>
                  </div>
                </div>

                <div className="project-progress">
                  <div className="progress-info">
                    <span className="progress-label">Overall Progress</span>
                    <span className="progress-percentage">38%</span>
                  </div>
                  <div className="progress" style={{ height: "8px" }}>
                    <div
                      className="progress-bar"
                      style={{
                        width: "38%",
                        background: "linear-gradient(90deg, #34c759, #30d158)",
                      }}
                    ></div>
                  </div>
                </div>

                <div className="project-footer">
                  <div className="project-meta-item">
                    <i className="fas fa-calendar"></i>
                    <span>Updated 1 day ago</span>
                  </div>
                </div>
              </div>
            </div>

            {/* E-commerce Platform Project */}
            <div className="project-card">
              <div
                className="project-color-bar"
                style={{ background: "#ff9500" }}
              ></div>
              <div className="project-card-content">
                <div className="project-header">
                  <h4 className="project-title">E-commerce Platform</h4>
                </div>
                <p className="project-description">
                  Full-stack e-commerce solution with payment integration and
                  inventory management
                </p>

                <div className="project-stats-row">
                  <div className="stat-card">
                    <div
                      className="stat-circle"
                      style={{
                        background:
                          "conic-gradient(#ff9500 0deg 48deg, rgba(255, 255, 255, 0.1) 48deg)",
                      }}
                    >
                      <div className="stat-circle-inner">
                        <span className="stat-number">2</span>
                        <span className="stat-total">/15</span>
                      </div>
                    </div>
                    <span className="stat-label">Tasks Done</span>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-card">
                    <div className="stat-icon-wrapper">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="stat-text">
                      <span className="stat-number">8h</span>
                      <span className="stat-label">Time Logged</span>
                    </div>
                  </div>
                </div>

                <div className="project-progress">
                  <div className="progress-info">
                    <span className="progress-label">Overall Progress</span>
                    <span className="progress-percentage">13%</span>
                  </div>
                  <div className="progress" style={{ height: "8px" }}>
                    <div
                      className="progress-bar"
                      style={{
                        width: "13%",
                        background: "linear-gradient(90deg, #ff9500, #ff3b30)",
                      }}
                    ></div>
                  </div>
                </div>

                <div className="project-footer">
                  <div className="project-meta-item">
                    <i className="fas fa-calendar"></i>
                    <span>Updated 3 hours ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Elements Section */}
      <section className="component-section">
        <h2 className="section-title">Form Elements</h2>

        <div className="component-group">
          <h3 className="group-title">Input Fields</h3>
          <div className="component-demo form-demo">
            <Form.Group className="mb-3">
              <Form.Label>Text Input</Form.Label>
              <Form.Control type="text" placeholder="Enter your name" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Input</Form.Label>
              <Form.Control type="email" placeholder="your@email.com" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Textarea</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter description..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select Dropdown</Form.Label>
              <Form.Select>
                <option>Select an option</option>
                <option value="1">Option 1</option>
                <option value="2">Option 2</option>
                <option value="3">Option 3</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date Input</Form.Label>
              <div className="date-input-wrapper">
                <Form.Control type="date" className="ios-date-input" />
              </div>
              <Form.Text className="text-muted">
                Select a date for scheduling
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>DateTime Input</Form.Label>
              <div className="date-input-wrapper">
                <Form.Control
                  type="datetime-local"
                  className="ios-date-input"
                />
              </div>
              <Form.Text className="text-muted">
                Choose date and time for events
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Color Picker</Form.Label>
              <div className="color-picker-grid">
                <div className="color-picker-item">
                  <Form.Control
                    type="color"
                    defaultValue="#007aff"
                    className="ios-color-picker"
                  />
                  <span className="color-label">Primary</span>
                </div>
                <div className="color-picker-item">
                  <Form.Control
                    type="color"
                    defaultValue="#34c759"
                    className="ios-color-picker"
                  />
                  <span className="color-label">Success</span>
                </div>
                <div className="color-picker-item">
                  <Form.Control
                    type="color"
                    defaultValue="#ff9500"
                    className="ios-color-picker"
                  />
                  <span className="color-label">Warning</span>
                </div>
                <div className="color-picker-item">
                  <Form.Control
                    type="color"
                    defaultValue="#ff3b30"
                    className="ios-color-picker"
                  />
                  <span className="color-label">Danger</span>
                </div>
                <div className="color-picker-item">
                  <Form.Control
                    type="color"
                    defaultValue="#af52de"
                    className="ios-color-picker"
                  />
                  <span className="color-label">Purple</span>
                </div>
                <div className="color-picker-item">
                  <Form.Control
                    type="color"
                    defaultValue="#ff2d55"
                    className="ios-color-picker"
                  />
                  <span className="color-label">Pink</span>
                </div>
              </div>
              <Form.Text className="text-muted">
                Choose colors for your project theme
              </Form.Text>
            </Form.Group>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Checkboxes & Switches</h3>
          <div className="component-demo form-demo">
            <Form.Check type="checkbox" id="check1" label="Default checkbox" />
            <Form.Check
              type="checkbox"
              id="check2"
              label="Checked checkbox"
              defaultChecked
            />
            <Form.Check type="switch" id="switch1" label="Default switch" />
            <Form.Check
              type="switch"
              id="switch2"
              label="Checked switch"
              defaultChecked
            />
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Radio Buttons</h3>
          <div className="component-demo form-demo">
            <Form.Check
              type="radio"
              id="radio1"
              name="radioGroup"
              label="Option 1"
              defaultChecked
            />
            <Form.Check
              type="radio"
              id="radio2"
              name="radioGroup"
              label="Option 2"
            />
            <Form.Check
              type="radio"
              id="radio3"
              name="radioGroup"
              label="Option 3"
            />
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Range Slider</h3>
          <div className="component-demo form-demo">
            <Form.Group className="mb-3">
              <Form.Label>Volume Control</Form.Label>
              <Form.Range defaultValue={50} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Opacity</Form.Label>
              <Form.Range defaultValue={75} min={0} max={100} />
            </Form.Group>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Number & Time Inputs</h3>
          <div className="component-demo form-demo">
            <Form.Group className="mb-3">
              <Form.Label>Number Input</Form.Label>
              <Form.Control type="number" placeholder="0" min={0} max={100} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Time Input</Form.Label>
              <Form.Control type="time" />
            </Form.Group>
          </div>
        </div>
      </section>

      {/* Modals Section */}
      <section className="component-section">
        <h2 className="section-title">Modals</h2>

        <div className="component-group">
          <h3 className="group-title">Work Modals</h3>
          <div className="component-demo">
            <Button variant="primary" onClick={() => setShowTaskModal(true)}>
              <i className="fas fa-plus"></i> New Task Modal
            </Button>
            <Button variant="primary" onClick={() => setShowProjectModal(true)}>
              <i className="fas fa-folder-plus"></i> New Project Modal
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowTaskDetailsModal(true)}
            >
              <i className="fas fa-eye"></i> Task Details Modal
            </Button>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Custom Modal</h3>
          <div className="component-demo">
            <Button variant="primary" onClick={() => setShowCustomModal(true)}>
              <i className="fas fa-circle-info"></i> Show Custom Modal
            </Button>
          </div>
        </div>
      </section>

      {/* Colors Section */}
      <section className="component-section">
        <h2 className="section-title">Color Palette</h2>

        <div className="component-group">
          <h3 className="group-title">iOS Colors</h3>
          <div className="color-palette">
            <div className="color-swatch">
              <div
                className="color-box"
                style={{ background: "#007aff" }}
              ></div>
              <div className="color-info">
                <strong>iOS Blue</strong>
                <code>#007aff</code>
              </div>
            </div>
            <div className="color-swatch">
              <div
                className="color-box"
                style={{ background: "#34c759" }}
              ></div>
              <div className="color-info">
                <strong>iOS Green</strong>
                <code>#34c759</code>
              </div>
            </div>
            <div className="color-swatch">
              <div
                className="color-box"
                style={{ background: "#ff9500" }}
              ></div>
              <div className="color-info">
                <strong>iOS Orange</strong>
                <code>#ff9500</code>
              </div>
            </div>
            <div className="color-swatch">
              <div
                className="color-box"
                style={{ background: "#ff3b30" }}
              ></div>
              <div className="color-info">
                <strong>iOS Red</strong>
                <code>#ff3b30</code>
              </div>
            </div>
            <div className="color-swatch">
              <div
                className="color-box"
                style={{ background: "#5856d6" }}
              ></div>
              <div className="color-info">
                <strong>iOS Purple</strong>
                <code>#5856d6</code>
              </div>
            </div>
            <div className="color-swatch">
              <div
                className="color-box"
                style={{ background: "#1c1c1e" }}
              ></div>
              <div className="color-info">
                <strong>Dark BG</strong>
                <code>#1c1c1e</code>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spacing Section */}
      <section className="component-section">
        <h2 className="section-title">Spacing & Shadows</h2>

        <div className="component-group">
          <h3 className="group-title">Shadow Levels</h3>
          <div className="shadow-demo">
            <div className="shadow-box shadow-sm">Small Shadow</div>
            <div className="shadow-box shadow">Default Shadow</div>
            <div className="shadow-box shadow-lg">Large Shadow</div>
          </div>
        </div>
      </section>

      {/* Typography Section */}
      <section className="component-section">
        <h2 className="section-title">Typography</h2>

        <div className="component-group">
          <h3 className="group-title">Headings</h3>
          <div className="typography-demo">
            <h1>Heading 1 - 34px Bold</h1>
            <h2>Heading 2 - 28px Bold</h2>
            <h3>Heading 3 - 22px Semibold</h3>
            <h4>Heading 4 - 18px Semibold</h4>
            <h5>Heading 5 - 16px Semibold</h5>
            <h6>Heading 6 - 14px Semibold</h6>
          </div>
        </div>

        <div className="component-group">
          <h3 className="group-title">Body Text</h3>
          <div className="typography-demo">
            <p className="lead">Lead paragraph - 17px Regular</p>
            <p>Regular paragraph - 15px Regular</p>
            <p className="text-muted">Muted text - 15px Regular</p>
            <p>
              <small>Small text - 13px Regular</small>
            </p>
          </div>
        </div>
      </section>

      {/* Modals */}
      <TaskModal
        show={showTaskModal}
        onHide={() => setShowTaskModal(false)}
        projects={sampleProjects}
        onSave={(task) => {
          console.log("Task saved:", task);
          setShowTaskModal(false);
        }}
      />

      <ProjectModal
        show={showProjectModal}
        onHide={() => setShowProjectModal(false)}
        onSave={(project) => {
          console.log("Project saved:", project);
          setShowProjectModal(false);
        }}
      />

      {showTaskDetailsModal && (
        <TaskDetailsModal
          task={sampleTask}
          projects={sampleProjects}
          show={showTaskDetailsModal}
          onHide={() => setShowTaskDetailsModal(false)}
          onStartTimer={(taskId) => console.log("Timer started:", taskId)}
          onStopTimer={(taskId) => console.log("Timer stopped:", taskId)}
          onEditTask={(task) => {
            console.log("Edit task:", task);
            setShowTaskDetailsModal(false);
            setShowTaskModal(true);
          }}
          onDeleteTask={(taskId) => {
            console.log("Task deleted:", taskId);
            setShowTaskDetailsModal(false);
          }}
          onUpdateTask={(taskId, updates) => {
            console.log("Task updated:", taskId, updates);
          }}
          onAddTimeEntry={(taskId, entry) =>
            console.log("Time entry added:", taskId, entry)
          }
          onUpdateTimeEntry={(taskId, entryId, updates) =>
            console.log("Time entry updated:", taskId, entryId, updates)
          }
          onDeleteTimeEntry={(taskId, entryId) =>
            console.log("Time entry deleted:", taskId, entryId)
          }
        />
      )}

      <Modal
        show={showCustomModal}
        onHide={() => setShowCustomModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Custom Modal Example</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This is a custom modal with the iOS glassmorphism design.</p>
          <p>It features backdrop blur, dark theme, and smooth animations.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => setShowCustomModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={() => setShowCustomModal(false)}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
